import { useState, useEffect } from "react";
import { Trash2, MapPin, Plus, Check } from "lucide-react";
import AdminCityManager from "../../services/dashboard/CityManager";
import toast from "react-hot-toast";

export default function AdminCityDashboard() {
  const [cities, setCities] = useState([]);
  const [selectedCityId, setSelectedCityId] = useState(null);
  const [pincodes, setPincodes] = useState([]);
  const [newCityName, setNewCityName] = useState("");
  const [newPincode, setNewPincode] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchCities();
  }, []);

  const fetchCities = async () => {
    try {
      setLoading(true);
      const list = await AdminCityManager.getAllCities();
      setCities(list);
    } catch (err) {
      toast.error("Failed to load cities");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCity = async (cityId) => {
    if (window.confirm("Are you sure you want to delete this city?")) {
      try {
        await AdminCityManager.deleteCity(cityId);
        toast.success("City deleted");
        setCities((prev) => prev.filter((c) => c.cityId !== cityId));
        if (selectedCityId === cityId) {
          setSelectedCityId(null);
          setPincodes([]);
        }
      } catch {
        toast.error("Failed to delete city");
      }
    }
  };

  const handleDeletePincode = async (pinId) => {
    if (window.confirm("Are you sure you want to delete this pincode?")) {
      try {
        await AdminCityManager.deletePincode(pinId);
        toast.success("Pincode deleted");
        setPincodes((prev) => prev.filter((p) => p.pinId !== pinId));
      } catch {
        toast.error("Failed to delete pincode");
      }
    }
  };

  const handleSelectCity = async (city) => {
    setSelectedCityId(city.cityId);
    try {
      const codes = await AdminCityManager.getPincodesByCity(city.cityId);
      setPincodes(codes || []);
    } catch {
      toast.error("Failed to load pincodes");
      setPincodes([]);
    }
  };

  const handleAddCity = async () => {
    if (!newCityName.trim()) return;
    try {
      const added = await AdminCityManager.addCity({ cityName: newCityName });
      setCities((prev) => [...prev, added]);
      setNewCityName("");
      toast.success("City added");
    } catch {
      toast.error("Failed to add city");
    }
  };

  const handleAddPincode = async () => {
    if (!newPincode.trim() || !selectedCityId) return;
    try {
      const added = await AdminCityManager.addCityPincode(selectedCityId, {
        pinCode: newPincode,
      });
      setPincodes((prev) => [...prev, added]);
      setNewPincode("");
      toast.success("Pincode added");
    } catch {
      toast.error("Failed to add pincode");
    }
  };

  const toggleCityStatus = async (cityId, currentStatus) => {
    try {
      await AdminCityManager.updateCityStatus(cityId, !currentStatus);
      await fetchCities(); // refresh list
      toast.success("City status updated");
    } catch {
      toast.error("Failed to update city status");
    }
  };

  const togglePincodeStatus = async (pinId, currentStatus) => {
    try {
      await AdminCityManager.updatePincodeStatus(pinId, !currentStatus);
      const updated = await AdminCityManager.getPincodesByCity(selectedCityId);
      setPincodes(updated || []);
      toast.success("Pincode status updated");
    } catch {
      toast.error("Failed to update pincode status");
    }
  };

  return (
    <div className="min-h-screen p-6 bg-gray-50 dark:bg-gray-950">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <header>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
            City & Pincode Management
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Manage operational cities and their pincodes
          </p>
        </header>

        {/* Two-Panel Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* LEFT PANEL: Cities */}
          <div className="lg:col-span-1 space-y-4">
            {/* Cities Section */}
            <div className="bg-white dark:bg-gray-900/60 rounded-xl border border-gray-200/60 dark:border-gray-800/80 shadow-sm overflow-hidden">
              {/* Section Header */}
              <div className="px-4 py-3 border-b border-gray-200/60 dark:border-gray-800/80 bg-gray-50/40 dark:bg-gray-900/40">
                <h2 className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest">
                  Cities
                </h2>
              </div>

              {/* Cities List */}
              <div className="p-3 space-y-2 max-h-[500px] overflow-y-auto custom-scrollbar">
                <style>{`
                  .custom-scrollbar::-webkit-scrollbar {
                    width: 6px;
                  }
                  .custom-scrollbar::-webkit-scrollbar-track {
                    background: transparent;
                  }
                  .custom-scrollbar::-webkit-scrollbar-thumb {
                    background-color: rgb(203 213 225 / 0.6);
                    border-radius: 3px;
                    transition: background-color 0.15s;
                  }
                  .dark .custom-scrollbar::-webkit-scrollbar-thumb {
                    background-color: rgb(71 85 105 / 0.8);
                  }
                  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background-color: rgb(148 163 184);
                  }
                  .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background-color: rgb(100 116 139);
                  }
                `}</style>
                {loading ? (
                  <div className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">
                    Loading cities...
                  </div>
                ) : cities.length === 0 ? (
                  <div className="text-center py-8">
                    <MapPin className="w-8 h-8 text-gray-300 dark:text-gray-700 mx-auto mb-2" />
                    <p className="text-xs text-gray-500 dark:text-gray-400">No cities added yet</p>
                  </div>
                ) : (
                  cities.map((city) => (
                    <div
                      key={city.cityId}
                      className={`group relative flex items-center justify-between px-4 py-3 rounded-lg transition-all duration-150 cursor-pointer ${
                        selectedCityId === city.cityId
                          ? "bg-orange-50/80 dark:bg-orange-900/20 border-l-[3px] border-orange-500 shadow-sm"
                          : "bg-white dark:bg-gray-800/60 hover:bg-gray-50 dark:hover:bg-gray-800/80 border-l-[3px] border-transparent"
                      }`}
                      onClick={() => handleSelectCity(city)}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <span
                          className={`text-sm font-medium truncate ${
                            selectedCityId === city.cityId
                              ? "text-gray-900 dark:text-gray-100"
                              : "text-gray-700 dark:text-gray-300"
                          }`}
                        >
                          {city.cityName}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-2.5">
                        {/* Custom Checkbox */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCityStatus(city.cityId, city.active);
                          }}
                          className={`relative w-4 h-4 rounded border-2 transition-all duration-200 flex items-center justify-center ${
                            city.active
                              ? "bg-emerald-500 border-emerald-500 dark:bg-emerald-600 dark:border-emerald-600"
                              : "bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500"
                          }`}
                          title={`Mark city as ${city.active ? "Inactive" : "Active"}`}
                        >
                          {city.active && (
                            <Check className="w-3 h-3 text-white" strokeWidth={3} />
                          )}
                        </button>

                        {/* Delete Icon - Show on hover */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteCity(city.cityId);
                          }}
                          className="opacity-0 group-hover:opacity-100 p-1 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-all duration-200"
                          title="Delete city"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Add City Input */}
              <div className="p-3 border-t border-gray-200/60 dark:border-gray-800/80">
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="New city name"
                    value={newCityName}
                    onChange={(e) => setNewCityName(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && handleAddCity()}
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-700/80 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 bg-white dark:bg-gray-800/60 dark:text-gray-100 transition-all duration-150"
                  />
                  <button
                    onClick={handleAddCity}
                    disabled={!newCityName.trim()}
                    className="px-3 py-2 rounded-lg bg-gray-900 hover:bg-gray-800 dark:bg-gray-100 dark:hover:bg-gray-200 text-white dark:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-150 shadow-sm"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT PANEL: Pincodes */}
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white dark:bg-gray-900/60 rounded-xl border border-gray-200/60 dark:border-gray-800/80 shadow-sm overflow-hidden">
              {/* Section Header */}
              <div className="px-4 py-3 border-b border-gray-200/60 dark:border-gray-800/80 bg-gray-50/40 dark:bg-gray-900/40">
                <h2 className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest">
                  Pincodes
                  {selectedCityId && (
                    <span className="ml-2 text-gray-400 dark:text-gray-500 font-normal">
                      ({cities.find((c) => c.cityId === selectedCityId)?.cityName})
                    </span>
                  )}
                </h2>
              </div>

              {selectedCityId ? (
                <>
                  {/* Pincodes Grid */}
                  <div className="p-4">
                    {pincodes.length === 0 ? (
                      <div className="text-center py-12">
                        <MapPin className="w-10 h-10 text-gray-300 dark:text-gray-700 mx-auto mb-3" />
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                          No pincodes added yet
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                          Add pincodes for this city below
                        </p>
                      </div>
                    ) : (
                      <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))' }}>
                        {pincodes.map((pc) => (
                          <div
                            key={pc.pinId}
                            className="group relative px-3 py-2.5 bg-white dark:bg-gray-800/60 rounded-lg border border-gray-200/60 dark:border-gray-700/60 hover:bg-gray-50 dark:hover:bg-gray-800/80 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-sm transition-all duration-150"
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span 
                                className="text-sm font-medium text-gray-900 dark:text-gray-100 tabular-nums tracking-wide"
                                title={pc.pinCode}
                              >
                                {pc.pinCode}
                              </span>
                              
                              <div className="flex items-center gap-1.5 flex-shrink-0">
                                {/* Custom Checkbox */}
                                <button
                                  onClick={() => togglePincodeStatus(pc.pinId, pc.active)}
                                  className={`w-3.5 h-3.5 rounded border-2 transition-all duration-200 flex items-center justify-center ${
                                    pc.active
                                      ? "bg-emerald-500 border-emerald-500 dark:bg-emerald-600 dark:border-emerald-600"
                                      : "bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500"
                                  }`}
                                  title={`Mark pincode as ${pc.active ? "Inactive" : "Active"}`}
                                >
                                  {pc.active && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
                                </button>

                                {/* Delete Icon - Show on hover */}
                                <button
                                  onClick={() => handleDeletePincode(pc.pinId)}
                                  className="opacity-0 group-hover:opacity-100 p-0.5 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-all duration-200"
                                  title="Delete pincode"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Add Pincode Input */}
                  <div className="p-4 border-t border-gray-200/60 dark:border-gray-800/80">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="New pincode"
                        value={newPincode}
                        onChange={(e) => setNewPincode(e.target.value)}
                        onKeyPress={(e) => e.key === "Enter" && handleAddPincode()}
                        className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-700/80 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 bg-white dark:bg-gray-800/60 dark:text-gray-100 transition-all duration-150"
                      />
                      <button
                        onClick={handleAddPincode}
                        disabled={!newPincode.trim()}
                        className="px-4 py-2 text-sm font-medium rounded-lg bg-gray-900 hover:bg-gray-800 dark:bg-gray-100 dark:hover:bg-gray-200 text-white dark:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-150 shadow-sm"
                      >
                        Add Pincode
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="p-12 text-center">
                  <MapPin className="w-12 h-12 text-gray-300 dark:text-gray-700 mx-auto mb-3" />
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Select a city to view pincodes
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    Choose a city from the left panel
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
